import { createContext } from 'react';
import { useSelector } from 'react-redux';
import { createContextualCan, useAbility as originalUseAbility } from '@casl/react';
import { Ability, AbilityBuilder, createAliasResolver } from '@casl/ability';

// COMPONENTS
import { Spinner } from 'components/Loading';
import NotFound from 'views/NotFound';

// CONSTANTS
import { FLEET_PLANS } from 'constants/plans';
import { USER_ROLES, UserPermission } from 'constants/common';

// SELECTORS
import { selectUser, selectLoading as selectAuthLoading } from 'services/Authentication/selectors';

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION LEGEND
//
// UserPermission (CRUD-style actions on a subject):
//   VIEW    — page/route visibility. Controls whether the screen renders at all
//             (typically checked via <CanOrNotFound I={VIEW} ...>). Distinct
//             from READ: VIEW gates the *view*, READ gates the *data*.
//   READ    — read the underlying resource/data. Part of the CRUD family.
//   CREATE  — create a new instance of the resource.
//   UPDATE  — modify an existing instance.
//   DELETE  — remove an instance.
//   MANAGE  — CASL's special "any action" keyword. Implicitly satisfies every
//             other check (VIEW, READ, CREATE, UPDATE, DELETE, and any alias).
//
// Aliases (resolved at check time via createAliasResolver below):
//   inspect → VIEW + READ
//             "Look but don't touch": route renders AND data loads.
//   modify  → UPDATE + DELETE
//             "Change or remove an existing instance."
//   access  → inspect + modify  (i.e. VIEW + READ + UPDATE + DELETE)
//             Full data access short of creating new instances.
//
// ─────────────────────────────────────────────────────────────────────────────

const resolveAction = createAliasResolver({
  inspect: [UserPermission.READ, UserPermission.VIEW],
  modify: [UserPermission.UPDATE, UserPermission.DELETE],
  access: [UserPermission.INSPECT, UserPermission.MODIFY],
});

const ability = new Ability([], {
  resolveAction,
});

export const AbilityContext = createContext(ability);

export const AbilityProvider = ({ children }) => (
  <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
);

/**
 * Component used to wrap user's ability defined through permissions
 */
export const Can = (props) => {
  const { loadingFallback } = props;
  const authLoading = useSelector(selectAuthLoading());

  const contextualCan = createContextualCan(AbilityContext.Consumer)(props);

  return loadingFallback != undefined && authLoading ? loadingFallback : contextualCan;
};

/**
 * Component which
 */
export const CanOrNotFound = (props) => (
  <Can I={props.I} a={props.a} field={props.field} loadingFallback={<Spinner />} passThrough>
    {(allowed) => (allowed ? props.children : <NotFound />)}
  </Can>
);

/**
 * `@casl/react`'s `useAbility` wrapped in the app's ability context
 */
export const useAbility = () => {
  const authLoading = useSelector(selectAuthLoading());
  const user = useSelector(selectUser());

  const originalContext = originalUseAbility(AbilityContext);

  // Augment CASL's useAbility with a loading state for the user's info.
  originalContext.ready = !authLoading && Boolean(user);

  return originalContext;
};

// Allows when the user's plan matches
const PlanBasedAbilityBuilder = (planId, allow) => (plan, action, subject, field?) => {
  if (plan.includes(planId)) {
    allow(action, subject, field);
  }
};

/**
 * Updates's the ability context
 */
export const updateAbility = (user) => {
  const { planId } = user.profile.dealership.settings;

  const { can: allow, rules } = new AbilityBuilder(Ability);
  const allowBasedOnPlan = PlanBasedAbilityBuilder(planId, allow);

  // Role-related permissions
  const { role } = user.profile;

  if (role === USER_ROLES.admin) {
    allow(UserPermission.MANAGE, 'agreements');
    allow(UserPermission.MANAGE, 'customers');
    allow(UserPermission.MANAGE, 'vehicles');
    allow(UserPermission.MANAGE, 'settings');
    allow(UserPermission.MANAGE, 'dashboard');

    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'planner');
    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'damage-picture');
    allowBasedOnPlan([FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'valet');
  } else if (role === USER_ROLES.user) {
    allow(UserPermission.MANAGE, 'agreements');
    allow(UserPermission.MANAGE, 'customers');
    allow(UserPermission.MANAGE, 'vehicles');

    allow(UserPermission.ACCESS, 'settings', 'telematics');

    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'planner');
    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'damage-picture');
    allowBasedOnPlan([FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'valet');
  } else if (role === USER_ROLES.readOnlyPlanner) {
    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.VIEW, 'planner');
    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.READ, 'planner', 'vehicles');
  } else if (role === USER_ROLES.driver) {
    allow(UserPermission.MANAGE, 'agreements');

    allowBasedOnPlan([FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'valet');
  } else if (role === USER_ROLES.signingCustomer) {
    allow([UserPermission.READ, UserPermission.UPDATE], 'agreements');
    allow([UserPermission.VIEW], 'customer-checkin');
  } else if (role === USER_ROLES.coordinator) {
    allow(UserPermission.MANAGE, 'agreements');
    allow(UserPermission.MANAGE, 'customers');

    allow(UserPermission.ACCESS, 'vehicles');
    allow(UserPermission.ACCESS, 'settings', 'telematics');

    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'planner');
    allowBasedOnPlan([FLEET_PLANS.pro, FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'damage-picture');
    allowBasedOnPlan([FLEET_PLANS.pro_plus], UserPermission.MANAGE, 'valet');
  }
  ability.update(rules);
};

export const withAbility = (Component) => (props) => {
  const ability = useAbility();
  return <Component {...props} ability={ability} />;
};

export default (action, subject) => ability.can(action, subject);
